import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';

const TIMEZONE_LIST_ASSET = 'assets/calendar-timezones/calendar-timezones.json';
const TIMEZONE_MAP_ASSET = 'assets/calendar-timezones/calendar-timezone-map.svg';
const UNSUPPORTED_TIMEZONE_IDS = [
  'Africa/Asmera', 'America/Atka', 'America/Buenos_Aires', 'America/Catamarca',
  'America/Coral_Harbour', 'America/Cordoba', 'America/Ensenada', 'America/Fort_Wayne',
  'America/Godthab', 'America/Indianapolis', 'America/Jujuy', 'America/Knox_IN',
  'America/Louisville', 'America/Mendoza', 'America/Montreal', 'America/Porto_Acre',
  'America/Rosario', 'America/Santa_Isabel', 'America/Shiprock', 'America/Virgin',
  'Asia/Ashkhabad', 'Asia/Calcutta', 'Asia/Chungking', 'Asia/Dacca', 'Asia/Harbin',
  'Asia/Istanbul', 'Asia/Katmandu', 'Asia/Macao', 'Asia/Rangoon', 'Asia/Saigon',
  'Asia/Tel_Aviv', 'Asia/Thimbu', 'Asia/Ujung_Pandang', 'Asia/Ulan_Bator',
  'Atlantic/Faeroe', 'Atlantic/Jan_Mayen', 'Australia/ACT', 'Australia/Canberra',
  'Australia/LHI', 'Australia/NSW', 'Australia/North', 'Australia/Queensland',
  'Australia/South', 'Australia/Tasmania', 'Australia/Victoria', 'Australia/West',
  'Australia/Yancowinna', 'Europe/Belfast', 'Europe/Kiev', 'Europe/Nicosia',
  'Europe/Tiraspol', 'Pacific/Enderbury', 'Pacific/Johnston', 'Pacific/Ponape',
  'Pacific/Samoa', 'Pacific/Truk', 'Pacific/Yap'
];

export interface CalendarTimezoneMapRegion {
  key: string;
  offsetHours: number;
  colorIndex: number;
  path: string;
}

export interface CalendarTimezoneData {
  zones: string[];
  mapViewBox: string;
  mapRegions: CalendarTimezoneMapRegion[];
}

@Injectable()
export class CalendarTimezoneDataService {
  private cachedData: CalendarTimezoneData;

  constructor(private http: HttpClient) {}

  public load(): Observable<CalendarTimezoneData> {
    if (this.cachedData) {
      return Observable.of(this.cachedData);
    }

    var mapRequest = this.http.get(TIMEZONE_MAP_ASSET, {
      responseType: 'text'
    }) as Observable<string>;

    return Observable.forkJoin([
      this.http.get<string[]>(TIMEZONE_LIST_ASSET),
      mapRequest
    ]).map((responses: [string[], string]) => {
      return buildCalendarTimezoneData(responses[0], responses[1]);
    }).do((data: CalendarTimezoneData) => {
      this.cachedData = data;
    });
  }
}

export function buildCalendarTimezoneData(zoneValues: string[], svgText: string): CalendarTimezoneData {
  var parsedMap = parseCalendarTimezoneMap(svgText);
  var zones = normalizeCalendarTimezones(zoneValues);

  zones.sort(compareText);
  return {
    zones: zones,
    mapViewBox: parsedMap.mapViewBox,
    mapRegions: parsedMap.mapRegions
  };
}

export function parseCalendarTimezoneMap(svgText: string): CalendarTimezoneData {
  var fallback: CalendarTimezoneData = {
    zones: [],
    mapViewBox: '0 0 960 420',
    mapRegions: []
  };

  if (!svgText || typeof DOMParser === 'undefined') {
    return fallback;
  }

  var documentNode = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (documentNode.getElementsByTagName('parsererror').length) {
    return fallback;
  }

  var svg = documentNode.documentElement;
  var pathNodes = svg.getElementsByTagName('path');
  var regions: CalendarTimezoneMapRegion[] = [];

  for (var index = 0; index < pathNodes.length; index++) {
    var pathNode = pathNodes.item(index);
    var offsetValue = normalizeText(pathNode.getAttribute('data-offset-hours'));
    var offsetHours = Number(offsetValue);
    var colorIndex = Number(normalizeText(pathNode.getAttribute('data-color-index')));
    var path = normalizeText(pathNode.getAttribute('d'));
    if (!offsetValue || isNaN(offsetHours) || !path) {
      continue;
    }
    regions.push({
      key: offsetHours + ':' + index,
      offsetHours: offsetHours,
      colorIndex: isNaN(colorIndex) ? 0 : colorIndex,
      path: path
    });
  }

  return {
    zones: [],
    mapViewBox: normalizeText(svg.getAttribute('viewBox')) || fallback.mapViewBox,
    mapRegions: regions
  };
}

export function filterCalendarTimezones(zones: string[], query: string, limit: number): string[] {
  var normalizedQuery = normalizeText(query).toLowerCase();
  var result: string[] = [];

  for (var index = 0; index < zones.length; index++) {
    if (normalizedQuery && zones[index].toLowerCase().indexOf(normalizedQuery) === -1) {
      continue;
    }
    result.push(zones[index]);
    if (result.length === limit) {
      break;
    }
  }
  return result;
}

function normalizeCalendarTimezones(zoneValues: string[]): string[] {
  if (!Array.isArray(zoneValues)) {
    return [];
  }

  var zones: string[] = [];
  zoneValues.forEach((value: string) => {
    var zoneId = normalizeText(value);
    if (!isIanaZoneId(zoneId) || zones.indexOf(zoneId) !== -1) {
      return;
    }
    zones.push(zoneId);
  });
  return zones;
}

function isIanaZoneId(zoneId: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_+.-]*(\/[A-Za-z0-9_+.-]+)+$/.test(zoneId) &&
    UNSUPPORTED_TIMEZONE_IDS.indexOf(zoneId) === -1;
}

function normalizeText(value: string): string {
  return typeof value === 'string' ? value.trim() : '';
}

function compareText(first: string, second: string): number {
  return first.localeCompare(second);
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/task-fullview-popup/calendar-timezone-data.service.ts